// Who is signed in, their family and the selected baby. Loaded once, refreshed on demand.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, getFamilies, getMe, type Baby, type Family, type Me } from './api';
import { startAnalytics } from './analytics';

/** no-access: signed in, but the account has no Baby Plan access (never bought, ended, or the inviting family's
 *  access ended). The app shows a plain screen with a way out instead of bouncing to /login. */
type Status = 'loading' | 'signed-out' | 'ready' | 'no-access';

type Session = {
  status: Status;
  me: Me | null;
  families: Family[];
  family: Family | null;
  baby: Baby | null;
  selectBaby: (id: string) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  setMe: (me: Me) => void;
};

const Ctx = createContext<Session | null>(null);
const BABY_KEY = 'bp:baby';
// The last session this phone loaded. Lets the Home Screen app open by the crib without signal or during a
// server hiccup (the Shhh button must always be reachable); only a 401/403 from the server means signed out.
const CACHE_KEY = 'bp:session-cache';

function readBaby(): string | null {
  try { return localStorage.getItem(BABY_KEY); } catch { return null; }
}
function readCache(): { me: Me; families: Family[] } | null {
  try { const c = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null'); return c?.me && Array.isArray(c.families) ? c : null; } catch { return null; }
}
/** The phone's own time zone. Every time on screen is shown in it: the parent's clock is the truth. */
export const deviceTimeZone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};
let tzSynced = false;
/** A family created in another time zone (or a parent who travelled): move it to the phone's zone, once a session,
 *  so the server's day boundaries and reminder times match the parent's clock. Owners and partners only. */
function syncTimezones(families: Family[]) {
  if (tzSynced) return;
  tzSynced = true;
  const tz = deviceTimeZone();
  for (const f of families) {
    if (f.timezone !== tz && (f.role === 'owner' || f.role === 'partner')) {
      void api(`/api/families/${f.id}`, { method: 'PATCH', body: { timezone: tz } }).catch(() => { tzSynced = false; });
    }
  }
}
function writeCache(me: Me | null, families: Family[]) {
  try {
    if (me) localStorage.setItem(CACHE_KEY, JSON.stringify({ me, families }));
    else {
      localStorage.removeItem(CACHE_KEY);
      // signed out: drop the saved plans too (usePlan › bp:plan:<baby>)
      Object.keys(localStorage).filter((k) => k.startsWith('bp:plan:')).forEach((k) => localStorage.removeItem(k));
    }
  } catch { /* private mode */ }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  // Open at once with the last session this phone saw (no splash on every reload); /api/me checks it in the
  // background and a 401/403 still signs out.
  const [boot] = useState(readCache);
  const [status, setStatus] = useState<Status>(boot ? 'ready' : 'loading');
  const [me, setMe] = useState<Me | null>(boot?.me ?? null);
  const [families, setFamilies] = useState<Family[]>(boot?.families ?? []);
  const [babyId, setBabyId] = useState<string | null>(readBaby);
  const meRef = useRef<Me | null>(null);
  meRef.current = me;
  const stale = useRef(!!boot); // showing the cached session; try again when the network comes back

  const signedOut = useCallback(() => {
    writeCache(null, []);
    setMe(null);
    setFamilies([]);
    setStatus('signed-out');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [u, f] = await Promise.all([getMe(), getFamilies()]);
      setMe(u);
      setFamilies(f);
      writeCache(u, f);
      stale.current = false;
      setStatus('ready');
      startAnalytics();
      syncTimezones(f);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403 && err.code === 'feature_required') {
        // /api/me answers without app access; only the family data needs it.
        const u = await getMe().catch(() => null);
        if (u) {
          writeCache(null, []);
          setMe(u);
          setFamilies([]);
          setStatus('no-access');
          startAnalytics();
          return;
        }
      }
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) { signedOut(); return; }
      // Offline or a server error: keep (or restore) the last known session instead of signing out.
      const cached = meRef.current ? null : readCache();
      if (meRef.current || cached) {
        if (cached) { setMe(cached.me); setFamilies(cached.families); }
        stale.current = true;
        setStatus('ready');
      } else {
        setStatus('signed-out');
      }
    }
  }, [signedOut]);

  useEffect(() => {
    if (boot) startAnalytics(); // the first screen's page_view comes before /api/me answers
    void refresh();
    const onUnauthorized = () => signedOut();
    const retry = () => { if (stale.current && document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('bp:unauthorized', onUnauthorized);
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      window.removeEventListener('bp:unauthorized', onUnauthorized);
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', retry);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    // The tracker keeps entries on the phone per user; drop them on sign-out.
    if (me) await import('@/features/track/store').then((m) => m.clearStore(me.id)).catch(() => undefined);
    signedOut();
  }, [me, signedOut]);

  const selectBaby = useCallback((id: string) => {
    setBabyId(id);
    try { localStorage.setItem(BABY_KEY, id); } catch { /* private mode */ }
  }, []);

  const value = useMemo<Session>(() => {
    // Screens format times with family.timezone: give them the phone's zone, so times match the parent's clock.
    const tz = deviceTimeZone();
    const local = families.map((f) => (f.timezone === tz ? f : { ...f, timezone: tz }));
    const allBabies = local.flatMap((f) => f.babies.map((b) => ({ b, f })));
    const hit = allBabies.find((x) => x.b.id === babyId) ?? allBabies[0] ?? null;
    return {
      status,
      me,
      families: local,
      family: hit?.f ?? local[0] ?? null,
      baby: hit?.b ?? null,
      selectBaby,
      refresh,
      logout,
      setMe,
    };
  }, [status, me, families, babyId, selectBaby, refresh, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

/**
 * Signed in, has a baby and finished onboarding (with consent): the app proper can render.
 * Someone who finished onboarding and later withdrew consent stays in the app: Learn, Sounds and Chat need no
 * consent, logs are refused by the server and Track offers to allow health data again. (Sending them back to
 * /welcome locked every tab on a consent screen with no way out.)
 */
export function needsOnboarding(s: Session): boolean {
  if (!s.me) return false;
  const completed = !!s.me.onboarding?.completedAt;
  return !s.baby || !completed;
}
